import { useState, useEffect } from "react";
import { ToolIcon, Menu } from "@theorgchartdev/storybook";
import LargeOptionContent from "../../../Blocks/Composites/LargeOptionContent/LargeOptionContent";
import WebLinkModal from "../../../Blocks/Modals/WebLinkModal/WebLinkModal";
import { WebLinkProps } from "./WebLinkUtils";
import { appstr } from "@coreapp/utils/appstr";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { useChartDocumentContext } from "@/providers/ChartDocumentProvider";

const WebLinkMenu = () => {
  const [showWLModal, setShowWLModal] = useState(false);
  const [linkType, setLinkType] = useState(null);
  const { canAccessWebLink } = usePermissionCheck();
  const { chartDocument } = useChartDocumentContext();
  const hasDoc = chartDocument?.state != null;

  // The modal only renders while a doc is loaded; if the doc goes away
  // while it's open, clear the flag so it can't reopen when a doc returns.
  useEffect(() => {
    if (!hasDoc && showWLModal) setShowWLModal(false);
  }, [hasDoc, showWLModal]);

  const webLinkTrigger = canAccessWebLink ? (
    <ToolIcon
      name={"link"}
      title={"Link Chart"}
      size="medium"
      aria-label={"Web link modal"}
      shape={"round"}
      disabled={!hasDoc}
      dataTestId={"web-link-modal-btn"}
    />
  ) : null;

  const openWLModal = value => {
    setLinkType(value);
    setShowWLModal(true);
  };

  const closeWLModal = () => {
    setShowWLModal(false);
  };

  const GetOptionContent = (label, description, icon, iconSize = "medium") => {
    return (
      <LargeOptionContent label={label} description={description} icon={icon} iconSize={iconSize} />
    );
  };

  const GetWebLinkMenuOptions = () => {
    let options = [];

    let publicOption = {
      content: GetOptionContent(
        appstr(WebLinkProps.public.label),
        appstr(WebLinkProps.public.description),
        WebLinkProps.public.icon
      ),
      value: "public",
      clickHandler: target => openWLModal(target.value),
      divider: true,
      dataTestId: "weblink-public-option"
    };
    let privateOption = {
      content: GetOptionContent(
        appstr(WebLinkProps.private.label),
        appstr(WebLinkProps.private.description),
        WebLinkProps.private.icon
      ),
      value: "private",
      clickHandler: target => openWLModal(target.value),
      divider: true,
      dataTestId: "weblink-private-option"
    };
    let restrictedOption = {
      content: GetOptionContent(
        appstr(WebLinkProps.restricted.label),
        appstr(WebLinkProps.restricted.description),
        WebLinkProps.restricted.icon
      ),
      value: "restricted",
      clickHandler: target => openWLModal(target.value),
      dataTestId: "weblink-restricted-option"
    };

    options.push(publicOption);
    options.push(privateOption);
    options.push(restrictedOption);

    return options;
  };

  if (!canAccessWebLink) {
    return null;
  }

  return (
    <div className="oc-weblink-menu">
      <Menu
        fillWidth={true}
        trigger={webLinkTrigger}
        optionSize={"large"}
        options={GetWebLinkMenuOptions()}
      />
      {showWLModal && hasDoc && (
        <WebLinkModal
          show={showWLModal}
          onClose={closeWLModal}
          linkType={linkType}
          titleProps={WebLinkProps[linkType]}
        />
      )}
    </div>
  );
};

export default WebLinkMenu;
